import React, { useRef, useState } from 'react';
import { cn } from '@utils/tailwind-class.merge';
import PulsarMenuBarOption from './PulsarMenuBarOption';
import { configDataSelector } from 'ducks/config/selectors';
import { useSelector } from 'react-redux';
import {
  getAppVersionLabel,
  getEnvironmentSuffix,
  lastCommit
} from '../../constants/config';

export type MenuItem = {
  icon: React.ReactNode;
  id: string;
  label: string | React.ReactNode;
  isNew?: boolean;
  badge?: React.ReactNode;
  metaData?: any;
  to?: string;
  externalLink?: boolean;
  onSelected?: () => void;
  options?: MenuItem[];
  onClick?: () => void;
};

export interface PulsarMenuBarProps {
  options: MenuItem[];
  selectedOptionId?: string;
  onOptionSelect?: (
    id: any,
    optionIndex?: number,
    subOptionIndex?: number
  ) => void;
  classNames?: string;
  avoidCollapse?: boolean;
  isNewText?: string;
}

function PulsarMenuBar(props: PulsarMenuBarProps) {
  const [expandIndex, setExpandIndex] = useState<number | null>(null);
  const componentRef = useRef(null);
  const mainOptionsRef = useRef<HTMLDivElement>(null);
  const userConfig = useSelector(configDataSelector);

  const onOptionExpanded = (index: number) => {
    setTimeout(() => {
      mainOptionsRef?.current?.scroll({
        top: index * 40,
        behavior: 'smooth'
      });
    }, 5);
  };

  return (
    <div
      className={cn(
        'tw-bg-background tw-z-10 tw-mr-0 tw-w-[4.5rem] tw-relative tw-h-full tw-transition-width tw-duration-300',
        !userConfig.isSidebarCollapsed && 'tw-w-60',
        props.classNames
      )}
    >
      <div
        className={cn(
          'tw-h-full tw-bg-background tw-shadow-sidebar tw-border-r tw-border-slate-900/10 tw-absolute tw-w-60  tw-top-0 tw-left-0 tw-z-50 tw-py-3 tw-px-4',
          userConfig.isSidebarCollapsed && 'tw-w-full',
          'tw-flex tw-flex-col tw-justify-between'
        )}
        ref={componentRef}
      >
        <div className="tw-flex tw-flex-col tw-justify-between tw-h-full">
          <div
            id="main-options-div"
            className="tw-overflow-y-auto tw-overflow-x-hidden tw-flex tw-flex-col tw-gap-[32px] [&::-webkit-scrollbar]:tw-hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            ref={mainOptionsRef}
          >
            <div className="tw-flex tw-flex-col tw-gap-2">
              {props.options.map((option, optionIndex) => (
                <PulsarMenuBarOption
                  data-testid={`menu-option-${option.id}`}
                  key={`option-list-${option.id}`}
                  selectedOptionId={props.selectedOptionId}
                  option={option}
                  isMenuCollapse={userConfig.isSidebarCollapsed}
                  onOptionSelect={props.onOptionSelect}
                  optionIndex={optionIndex}
                  isExpandedOption={optionIndex === expandIndex}
                  setExpandIndex={setExpandIndex}
                  isNewText={props.isNewText}
                  onOptionExpanded={onOptionExpanded}
                  onClick={option.onClick}
                />
              ))}
            </div>
          </div>
          <div
            className={cn(
              'tw-sticky tw-bottom-0 tw-text-center tw-text-xs tw-text-gray-400 tw-mt-4',
              userConfig.isSidebarCollapsed && 'tw-hidden'
            )}
          >
            <p>{`${getAppVersionLabel()}${getEnvironmentSuffix()}`}</p>
            {import.meta.env.DEV && <p>{lastCommit}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PulsarMenuBar;
